import { lazy, type ReactNode } from 'react';
import { Navigate, useParams, useSearchParams, type RouteObject } from 'react-router-dom';

import { can, RequireFlag, useEntitlements, type Capability } from '@core/access';
import { Loading } from '@ui/Loading';
import { RemountOn } from '@shared/components/remount-on/RemountOn';

/**
 * The CRM routes: companies and contacts under `/crm`, the firm's people
 * under `/people`, bids under `/opportunities` and won engagements under
 * `/projects`. Gated on
 * capabilities, like every other subtree - a convenience for rendering the
 * right failure, not a security boundary.
 */

const ClientIntelligencePage = lazy(() => import('./pages/ClientIntelligencePage'));
const CompaniesContactsPage = lazy(() => import('./pages/CompaniesContactsPage'));
const CompanyPage = lazy(() => import('./pages/CompanyPage'));
const PeoplePage = lazy(() => import('./pages/PeoplePage'));
const BidsBoardPage = lazy(() => import('./pages/BidsBoardPage'));
const BidOnJobPage = lazy(() => import('./pages/BidOnJobPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const ProjectViewPage = lazy(() => import('./pages/ProjectViewPage'));
const OpportunityRecordPage = lazy(() => import('./pages/OpportunityRecordPage'));
const OpportunityViewPage = lazy(() => import('./pages/OpportunityViewPage'));

function Guard({ capability, children }: { capability: Capability; children: ReactNode }) {
  const entitlements = useEntitlements();

  // Wait for sign-in to resolve rather than bouncing on the empty grant.
  if (entitlements.pending) {
    return <Loading variant="page" label="Checking access" />;
  }

  return can(entitlements, capability) ? <>{children}</> : <Navigate to="/404" replace />;
}

/**
 * `/crm` as it used to be addressed (`?tab=companies|contacts|people`), so
 * bookmarks land on the screen that now holds that list.
 */
function LegacyCrmRedirect() {
  const [params] = useSearchParams();
  const tab = params.get('tab');

  if (tab === 'people') {
    return <Navigate to="/people" replace />;
  }

  const next = new URLSearchParams(params);
  if (tab !== 'contacts') next.delete('tab');
  const query = next.toString();
  return <Navigate to={`/crm/companies${query ? `?${query}` : ''}`} replace />;
}

function LegacyProjectRedirect() {
  const { id } = useParams();
  return <Navigate to={`/projects/${id ?? ''}`} replace />;
}

export const crmRoutes: RouteObject[] = [
  {
    path: 'crm',
    children: [
      { index: true, element: <Guard capability="crm.view"><LegacyCrmRedirect /></Guard> },
      { path: 'intelligence', element: <RequireFlag flag="clientIntelligence"><Guard capability="crm.view"><ClientIntelligencePage /></Guard></RequireFlag> },
      { path: 'companies', element: <Guard capability="crm.view"><CompaniesContactsPage /></Guard> },
      { path: 'companies/:id', element: <Guard capability="crm.view"><CompanyPage /></Guard> },
      { path: 'companies/:id/:tab', element: <Guard capability="crm.view"><CompanyPage /></Guard> }
    ]
  },
  { path: 'people', element: <Guard capability="crm.view"><PeoplePage /></Guard> },
  {
    path: 'opportunities',
    children: [
      { index: true, element: <Guard capability="opportunities.view"><BidsBoardPage /></Guard> },
      // The named screens before `:id`, so they are not looked up as records.
      { path: 'new', element: <Guard capability="opportunities.edit"><OpportunityRecordPage /></Guard> },
      // An existing bid's RFP and detail; new work starts at `new`.
      { path: 'bid', element: <Guard capability="opportunities.view"><BidOnJobPage /></Guard> },
      // Projects moved to `/projects`; keep old links working.
      { path: 'projects', element: <Navigate to="/projects" replace /> },
      { path: 'projects/:id', element: <LegacyProjectRedirect /> },
      // A row opens the record view; the form is its Edit.
      { path: ':id', element: <Guard capability="opportunities.view"><RemountOn param="id"><OpportunityViewPage /></RemountOn></Guard> },
      { path: ':id/edit', element: <Guard capability="opportunities.edit"><RemountOn param="id"><OpportunityRecordPage /></RemountOn></Guard> }
    ]
  },
  {
    path: 'projects',
    children: [
      { index: true, element: <Guard capability="opportunities.view"><ProjectsPage /></Guard> },
      { path: ':id', element: <Guard capability="opportunities.view"><RemountOn param="id"><ProjectViewPage /></RemountOn></Guard> }
    ]
  }
];
